import { isEmpty, isNull } from 'rde-view-common/scripts/functions';

/**
 * 現在時刻をJST(YYYY-MM-DD hh:mm:ss JST)形式で返却します。
 * @param { boolean } jstHidden falseなら' JST'を表示、trueなら' JST'を非表示とする。(default: false）
 * @param { boolean } timeHidden falseなら時間を表示、trueなら時間を非表示とする。(default: false）
 *
 * @return {'2021-11-10 16:32:38 JST' | '2021-11-10 JST'} 現在時刻
 */
export function getCurrentJst(jstHidden = false, timeHidden = false) {
  return convertIsoStrToJstStr(new Date().toISOString(), false, timeHidden);
}

/**
 * UTC形式のAPIレスポンスをJST形式の文字列に変換します。
 *
 * @param str UTC形式の日付文字列 ('2021-11-01T11:22:01.333Z')
 * @param { boolean } jstHidden falseなら' JST'を表示、trueなら' JST'を非表示とする。(default: false）
 * @param { boolean } timeHidden falseなら時間を表示、trueなら時間を非表示とする。(default: false）
 * @returns { undefined | string }
 *     画面出力形式の日付文字列('2021-11-01 20:22:01 JST' | '2021-11-01 20:22:01' | '2021-11-01 JST' | '2021-11-01')
 */
export function convertIsoStrToJstStr(str, jstHidden = false, timeHidden = false) {
  if (isNull(str)) return;
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return;
  const diff = date.getTimezoneOffset() * 60000;
  const convertedDate = new Date(date.getTime() + diff + 32400000);

  const dateAry = getDateAry(convertedDate);
  const timeAry = getTimeAry(convertedDate);
  const dateStr = dateAry.join('-');
  const nonJst = timeHidden === true ? dateStr : dateStr + ' ' + timeAry.join(':');
  return jstHidden === true ? nonJst : nonJst + ' JST';
}

/**
 * UTC形式の時刻文字列をJST形式の時刻文字列に変換します。
 *
 * @param str UTC形式の時刻文字列 ('11:22:01.333Z')
 * @param { boolean } jstHidden falseなら' JST'を表示、trueなら' JST'を非表示とする。(default: false）
 * @returns { undefined | string } 画面出力形式の日付文字列('20:22:01 JST' | '20:22:01')
 */
export function convertIsoStrToJstStrTime(str, jstHidden = false) {
  if (isNull(str)) return;
  const jstStr = convertIsoStrToJstStr('2000-01-01T' + str, jstHidden);
  return jstStr === undefined ? undefined : jstStr.substring(11, jstStr.length).trim();
}

/**
 * 画面表示形式の文字列をUTC形式の文字列に変換します。
 *
 * @param str { string } "yyyy-MM-dd HH:mm:ss" 形式 String型
 * @return { undefined | '2021-10-31T15:00:00.000Z'} YYYY-MM-DDThh:mm:ss.sssZ形式 UTC日時 String型
 */
export function convertFormStrToIsoStr(str) {
  if (isNull(str)) return;
  let newStr = str;
  if (/^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(str)) {
    newStr = str + ' 12:00:00';
  }
  newStr = newStr.replaceAll('-', '/');
  const date = new Date(newStr);
  if (Number.isNaN(date.getTime())) return;
  const diff = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - diff - 32400000).toISOString();
}

/**
 * 画面表示形式の時刻文字列をUTC形式の時刻文字列に変換します。
 *
 * @param str { string } "HH:mm:ss" 形式 String型
 * @return { undefined | '15:00:00.000Z'} hh:mm:ss.sssZ形式 UTC時刻 String型
 */
export function convertFormStrToIsoStrTime(str, jstHidden = false) {
  if (isEmpty(str)) return;
  const utcStr = convertFormStrToIsoStr('2000-01-01 ' + str);
  return utcStr === undefined ? undefined: utcStr.substring(11, utcStr.length);
}

/**
 * 渡された日時を配列に格納して返却します。
 *
 * @param date { Date }  date型
 * @return {['yyyy','MM','dd']} 2桁の0埋め処理を行った年月日が格納された配列 String型配列
 */
function getDateAry(date) {
  return [
    date.getFullYear(),
    (date.getMonth() + 1).toString().padStart(2, '0'),
    date.getDate().toString().padStart(2, '0')
  ];
}

/**
 * 渡された日時を配列に格納して返却します。
 *
 * @param {*} time date型
 * @return {['HH', 'mm', 'ss']} 2桁の0埋め処理を行った時分秒が格納された配列 String型配列
 */
function getTimeAry(time) {
  return [
    time.getHours().toString().padStart(2, '0'),
    time.getMinutes().toString().padStart(2, '0'),
    time.getSeconds().toString().padStart(2, '0')
  ];
}


/**
 * yyyy-MM-dd形式の文字列に'0:00:00.000'を付加し、ISO形式の日付文字列として返却します。
 *
 * @param dateStr yyyy-MM-dd形式
 * @returns {"yyyy-MM-ddTHH:mm:ss.sssZ"|undefined}
 */
export function paddingDate(dateStr) {
  if (isNull(dateStr)) return;
  let newStr;
  switch (true) {
    case /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(dateStr):
      newStr = dateStr + ' 0:00:00';
      break;
    default:
      return undefined;
  }
  return convertFormStrToIsoStr(newStr);
}

/**
 * yyyy-MM-dd形式の文字列に'23:59:59.999'を付加し、ISO形式の日付文字列として返却します。
 *
 * @param dateStr yyyy-MM-dd形式
 * @returns {"yyyy-MM-ddTHH:mm:ss.sssZ"|undefined}
 */
export function upperPaddingDate(dateStr) {
  if (isNull(dateStr)) return;
  let newStr;
  switch (true) {
    case /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(dateStr):
      newStr = dateStr + ' 23:59:59';
      break;
    default:
      return undefined;
  }
  return convertFormStrToIsoStr(newStr);
}

/**
 * java.time.OffsetDateTimeでサポートされている最大のOffsetDateTimeを返却します。
 *
 * @param dateStr APIから返却された値
 * @returns {boolean} dateStrが'+999999999-12-31T23:59:59.999999999-18:00'の場合true
 */
export function isInfinity(dateStr) {
  return dateStr === '+999999999-12-31T23:59:59.999999999-18:00';
}
